import React from 'react';
import {Button, Modal, Spinner} from 'react-bootstrap';
import {useColor} from "../../contexts/ColorContext";
import SimpleBar from "simplebar-react";
import Loader from "../Loader";
import {useSafeBackNavigation} from "../../hooks/useSafeBackNavigation";

interface ModalWidgetProps {
    show?: boolean;
    onHide?: () => void;
    onAction?: () => void;
    onBack?: () => void;
    title?: string;
    backButtonTitle?: string;
    actionTitle?: string;
    actionButtonColor?: string;
    description?: React.ReactNode;
    headerIcon?: React.ElementType;
    actionIcon?: React.ElementType;
    isScrollable?: boolean;
    /** SimpleBar viewport height when isScrollable (default 400px). */
    scrollHeight?: number | string;
    hasFooter?: boolean;
    hasActionButton?: boolean;
    navigateBack?: boolean;
    loading?: boolean;
    actionLoading?: boolean;
    actionCompleted?: boolean;
    actionDisabled?: boolean;
    hasBack?: boolean;
    size?: any;
    className?: string;
    children?: React.ReactNode;
}

function ModalWidget ({
    show = false,
    onHide = ()=>{},
    onBack = ()=>{},
    children,
    title = '',
    description = '',
    backButtonTitle = 'Close',
    onAction = ()=>{},
    actionTitle = 'Next',
    actionIcon,
    headerIcon,
    isScrollable = false,
    scrollHeight = 400,
    size,
    actionButtonColor,
    hasFooter = true,
    hasActionButton = true,
    navigateBack = true,
    loading = false,
    actionLoading = false,
    actionCompleted = false,
    actionDisabled = false,
    hasBack = true,
    className
}: ModalWidgetProps){
    const {colors} = useColor()
    const ActionIcon = actionIcon as React.ElementType
    const HeaderIcon = headerIcon as React.ElementType
    const {goBack} = useSafeBackNavigation('/');

    const onClose = ()=>{
        if(navigateBack) {
            goBack()
        }else{
            onHide()
        }
    }

    const onBackButton = () => {
        if (navigateBack) {
            goBack();
            return;
        }
        onBack();
    };

    const body = <>
            {
                description &&
                <div className='font-sm font-bold-light mb-3'>
                    {description}
                </div>
            }

            {children}
        </>


    return (
        <Modal
            className={className}
            show={show}
            onHide={onClose}
            keyboard
            centered
            size={size}
            enforceFocus={false}
        >

            <Modal.Header closeButton>
                {headerIcon &&
                    <>
                        <HeaderIcon/>
                        <div className='mx-2'/>
                    </>
                }
                <Modal.Title className='font-md font-bold-light'>{loading? 'Loading...': title}</Modal.Title>
            </Modal.Header>
            {
                loading? <Loader/>:
                <>
                    <Modal.Body>
                        {
                            isScrollable?
                                <SimpleBar style={{ height: typeof scrollHeight === 'number' ? `${scrollHeight}px` : scrollHeight }}>
                                    {body}
                                </SimpleBar> : <>{body}</>
                        }

                    </Modal.Body>
                    {hasFooter &&
                        <Modal.Footer className='d-flex justify-content-between'>
                            {hasBack &&
                            <Button type="button"
                                    variant="outline-dark"
                                    onClick={()=>onBackButton()}
                                    className='font-bold'>
                                {backButtonTitle}
                            </Button>
                            }

                            {
                                hasActionButton &&
                                <Button
                                    type="button"
                                    onClick={onAction}
                                    className='border-0'
                                    disabled={actionLoading || actionCompleted || actionDisabled}
                                    style={{backgroundColor: actionCompleted ? '#28a745' : (actionButtonColor? actionButtonColor: colors.primary)}}>
                                    <div className='d-flex font-bold text-white align-items-center'>
                                        {actionLoading ? (
                                            <><Spinner animation="border" size="sm" className="me-2" />Saving...</>
                                        ) : actionCompleted ? (
                                            <><span className="me-1">{"\u2713"}</span>Completed</>
                                        ) : (
                                            <>{actionTitle}<div className='mx-1'/>{actionIcon && <ActionIcon/>}</>
                                        )}
                                    </div>
                                </Button>
                            }

                        </Modal.Footer>
                    }
                </>
            }

        </Modal>
    );
}

export default ModalWidget;
